import { useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { MapPin, Phone, Camera, Loader2, Sparkles, Trash2, Plus, FileText, CheckCircle2, ClipboardList, ListChecks, AlertCircle, Navigation } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errorMessage, fileUrl } from "@/lib/api";
import { useInstallerJob, useDeliveryUpdater, useSession, qk } from "@/lib/queries";
import { INSTALL_STATUS_LABELS, formatDate, formatDateTime, addressLine } from "@/lib/format";
import { LoadingBlock, ErrorBlock, SpecTable, Callout } from "@/components/common/Blocks";
import { StateChip, toneForInstallStatus, Avatar } from "@/components/common/Badges";
import { AiPanel, AiSource } from "@/components/common/AiPanel";
import { DrawingViewer } from "@/components/common/Viewers";
import { InstallerShell } from "@/components/layout/InstallerShell";

const TYPE_LABEL = { mangel: "Mangel", skade: "Skade", oppfolging: "Oppfølging" };

const Card = ({ title, icon: Icon, right, children, testId, className = "" }) => (
  <section className={`frame p-4 ${className}`} data-testid={testId}>
    {(title || right) && (
      <div className="mb-3 flex items-center justify-between gap-3">
        {title && <p className="inline-flex items-center gap-2 text-[14px] font-semibold tracking-tight">{Icon && <Icon className="h-4 w-4 text-muted-foreground" />} {title}</p>}
        {right}
      </div>
    )}
    {children}
  </section>
);

export default function MontorJobbPage() {
  const { deliveryId } = useParams();
  const qc = useQueryClient();
  const update = useDeliveryUpdater();
  const { data: job, isLoading, isError, refetch } = useInstallerJob(deliveryId);
  const { data: session } = useSession();
  const aiOn = session?.ai?.configured;
  const [note, setNote] = useState(null);
  const [result, setResult] = useState("");
  const [performed, setPerformed] = useState([]);
  const [remaining, setRemaining] = useState([]);
  const [aiMeta, setAiMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [basisOpen, setBasisOpen] = useState(false);
  const fileRef = useRef(null);

  if (isLoading) return <InstallerShell title="Jobb" back="/montor"><LoadingBlock /></InstallerShell>;
  if (isError || !job) return <InstallerShell title="Jobb" back="/montor"><ErrorBlock message="Fant ikke jobben." onRetry={refetch} /></InstallerShell>;

  const inst = job.installation || {};
  const done = ["montert", "montert_med_restpunkter"].includes(inst.status);
  const noteValue = note ?? inst.note ?? inst.note_draft ?? "";
  const usingDraft = note === null && !inst.note && !!inst.note_draft;

  const refresh = () => { qc.invalidateQueries({ queryKey: qk.installerJob(deliveryId) }); qc.invalidateQueries({ queryKey: qk.installerJobs }); };

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post(`/installer/jobs/${deliveryId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Bilde lagret");
      refresh();
      update(null);
    } catch (err) {
      toast.error(errorMessage(err, "Opplasting feilet"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const draft = async () => {
    if (noteValue.trim().length < 5) { toast.error("Skriv et kort notat først."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/draft-report", { delivery_id: deliveryId, note: noteValue });
      const s = data.suggestion;
      setPerformed(s.utfort_arbeid || []);
      setRemaining((s.restpunkter || []).map((r) => ({ title: r.tittel, description: r.beskrivelse, type: r.type || "oppfolging", source: r.kilde })));
      setResult(s.foreslatt_resultat || "");
      setAiMeta({ summary: s.oppsummering, uncertain: s.uavklart || [] });
      if (note === null) setNote(noteValue);
    } catch (err) {
      toast.error(errorMessage(err, "KI-utkastet feilet"));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!result) { toast.error("Velg resultat."); return; }
    if (!noteValue.trim()) { toast.error("Skriv et notat."); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/installer/jobs/${deliveryId}/report`, {
        note: noteValue, result, performed: performed.filter(Boolean), remaining: remaining.filter((r) => r.title.trim()).map((r) => ({ title: r.title, description: r.description, type: r.type })),
        origin: aiMeta ? "ki_redigert" : "manuell", installer: inst.installer,
      });
      update(data.delivery);
      refresh();
      toast.success(result === "ikke_fullfort" ? "Status lagret" : "Montasjerapport lagret", { description: data.service_cases.length ? `${data.service_cases.length} restpunkt sendt til Service: ${data.service_cases.map((s) => s.number).join(", ")}` : undefined });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const photos = inst.photos || [];

  return (
    <InstallerShell title={`${job.number} · ${job.project_name}`} back="/montor">
      <div className="space-y-4">
        <Card testId="installer-job-header">
          <p className="text-[11.5px] text-muted-foreground"><span className="font-mono">{job.number}</span> · {job.customer_name}</p>
          <h1 className="mt-0.5 text-[22px] font-semibold leading-tight tracking-tight">{job.project_name}</h1>
          <p className="text-sm text-muted-foreground">{job.stair_summary}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-lg border px-2 py-1 font-mono font-medium surface-subtle">{inst.planned_date ? formatDate(inst.planned_date) : "–"}{inst.time_window ? ` · ${inst.time_window}` : ""}</span>
            <StateChip tone={toneForInstallStatus(inst.status)}>{INSTALL_STATUS_LABELS[inst.status] || inst.status}</StateChip>
            {inst.installer && <span className="inline-flex items-center gap-1.5 text-muted-foreground"><Avatar name={inst.installer} size="xs" /> {[inst.installer, ...(inst.crew || [])].join(", ")}</span>}
          </div>
          <div className="mt-4 grid grid-cols-1 gap-2 text-sm">
            <a href={`https://www.google.com/maps/search/${encodeURIComponent(addressLine(job.address))}`} target="_blank" rel="noreferrer" className="flex min-h-[48px] items-center gap-3 rounded-md border px-3.5 py-2 transition-colors active:bg-secondary" data-testid="installer-address-link"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><MapPin className="h-4 w-4" /></span> <span className="min-w-0 flex-1 truncate">{addressLine(job.address)}</span><Navigation className="h-4 w-4 text-muted-foreground" /></a>
            {job.site_contact && <a href={`tel:${(job.site_contact.phone || "").replace(/\s/g, "")}`} className="flex min-h-[48px] items-center gap-3 rounded-md border px-3.5 py-2 transition-colors active:bg-secondary" data-testid="installer-contact-link"><span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary"><Phone className="h-4 w-4" /></span> <span className="min-w-0 flex-1 truncate">{job.site_contact.name}{job.site_contact.phone ? ` · ${job.site_contact.phone}` : ""}</span></a>}
          </div>
          {inst.practical_notes && <div className="mt-3 rounded-md surface-sunken p-3.5 text-sm"><p className="t-label mb-1">Praktisk</p>{inst.practical_notes}</div>}
        </Card>

        <Card testId="installer-released-basis">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="inline-flex items-center gap-2 text-[14px] font-semibold tracking-tight"><FileText className="h-4 w-4 text-muted-foreground" /> Frigitt arbeidsgrunnlag</p>
              <p className="mt-0.5 text-xs text-muted-foreground">{job.released_basis.spec_code ? `Spesifikasjon rev. ${job.released_basis.spec_code} · frigitt ${formatDate(job.released_basis.released_at)} · ${job.released_basis.drawings.length} tegning(er)` : "Ingen frigitt spesifikasjon – kontakt kontoret"}</p>
            </div>
            <Button variant="outline" className="touch-btn shrink-0" onClick={() => setBasisOpen(true)} disabled={!job.released_basis.spec_code} data-testid="installer-open-basis">Åpne</Button>
          </div>
        </Card>

        <Card title={`Bilder (${photos.length})`} icon={Camera} testId="installer-photos"
          right={
            <label className="inline-flex">
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="sr-only" onChange={upload} disabled={uploading || done} data-testid="installer-photo-upload-input" />
              <span className={`inline-flex h-10 cursor-pointer items-center gap-2 rounded-md border px-3.5 text-sm font-medium transition-colors ${done ? "opacity-50" : "hover:bg-secondary active:bg-secondary"}`}>{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />} Ta / velg bilde</span>
            </label>
          }>
          {photos.length ? <div className="grid grid-cols-3 gap-2">{photos.map((p) => <a key={p.id} href={fileUrl(p.id)} target="_blank" rel="noreferrer" className="group block overflow-hidden rounded-md border"><img src={fileUrl(p.id)} alt={p.name} className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-105" /></a>)}</div> : <p className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">Ingen bilder ennå. Bilder lagres på leveransen og vises i Service ved behov.</p>}
        </Card>

        {done ? (
          <Callout tone="released" icon={CheckCircle2} title={INSTALL_STATUS_LABELS[inst.status]} testId="installer-report-done"
            action={<Button asChild variant="outline" className="touch-btn"><Link to={`/service?leveranse=${deliveryId}`} data-testid="installer-go-to-service">Se restpunkter i Service</Link></Button>}>
            Rapport lagret {formatDateTime(inst.report?.saved_at)}.{inst.report?.remaining?.length ? ` ${inst.report.remaining.length} restpunkt er sendt til Service.` : ""}
            {inst.report && <div className="mt-3 space-y-3 text-sm"><div><p className="t-label mb-1">Utført</p><ul className="list-disc space-y-0.5 pl-5">{inst.report.performed.map((p, i) => <li key={i}>{p}</li>)}</ul></div>{inst.report.remaining.length > 0 && <div><p className="t-label mb-1">Restpunkter</p><ul className="list-disc space-y-0.5 pl-5">{inst.report.remaining.map((r, i) => <li key={i}>{r.title}</li>)}</ul></div>}</div>}
          </Callout>
        ) : (
          <Card title="Rapport" icon={ClipboardList} testId="installer-report-form" className="space-y-5">
            <div>
              <Label htmlFor="inst-note" className="text-sm font-semibold">Notat</Label>
              {usingDraft && <p className="mt-0.5 text-xs" style={{ color: "hsl(var(--state-waiting))" }}>Eksempelnotat forhåndsutfylt – rediger fritt.</p>}
              <Textarea id="inst-note" rows={4} value={noteValue} onChange={(e) => setNote(e.target.value)} className="mt-1.5 text-base leading-relaxed" placeholder="Hva er gjort, hva mangler, hva sa kunden?" data-testid="installer-note-input" />
              <Button variant="outline" className="touch-btn mt-2 w-full" onClick={draft} disabled={busy || !aiOn} data-testid="installer-ai-draft-button">{busy ? <Loader2 className="animate-spin" /> : <Sparkles />} {busy ? "Lager utkast…" : "Lag rapportutkast med KI"}</Button>
              {!aiOn && <p className="mt-1 text-xs text-muted-foreground">KI er ikke konfigurert – fyll ut rapporten manuelt.</p>}
            </div>

            {aiMeta && (
              <AiPanel title="Rapportutkast" meta="Kontroller og rediger før lagring" testId="installer-ai-report-panel">
                <p>{aiMeta.summary}</p>
                {aiMeta.uncertain.length > 0 && <div><p className="text-xs font-semibold">Uavklart</p><ul className="mt-1 list-disc space-y-0.5 pl-5">{aiMeta.uncertain.map((u, i) => <li key={i}>{u}</li>)}</ul></div>}
                <p className="text-xs text-muted-foreground">Punktene er lagt inn i skjemaet under. Fjern eller endre det som ikke stemmer.</p>
              </AiPanel>
            )}

            <div>
              <div className="flex items-center justify-between"><Label className="inline-flex items-center gap-2 text-sm font-semibold"><ListChecks className="h-4 w-4 text-muted-foreground" /> Utført arbeid</Label><Button variant="ghost" size="sm" onClick={() => setPerformed([...performed, ""])} data-testid="installer-add-performed"><Plus /> Legg til</Button></div>
              <div className="mt-2 space-y-2">
                {performed.map((p, i) => <div key={i} className="flex gap-2"><Input value={p} onChange={(e) => setPerformed(performed.map((x, j) => (j === i ? e.target.value : x)))} className="input-lg" data-testid={`installer-performed-${i}`} /><Button variant="ghost" size="icon" className="h-12 w-12 shrink-0" aria-label="Fjern" onClick={() => setPerformed(performed.filter((_, j) => j !== i))}><Trash2 /></Button></div>)}
                {!performed.length && <p className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">Ingen punkter ennå.</p>}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between"><Label className="inline-flex items-center gap-2 text-sm font-semibold"><AlertCircle className="h-4 w-4 text-muted-foreground" /> Restpunkter</Label><Button variant="ghost" size="sm" onClick={() => setRemaining([...remaining, { title: "", description: "", type: "oppfolging" }])} data-testid="installer-add-remaining"><Plus /> Legg til</Button></div>
              <p className="text-xs text-muted-foreground">Hvert restpunkt blir en egen servicesak knyttet til denne trappen.</p>
              <div className="mt-2 space-y-3">
                {remaining.map((r, i) => (
                  <div key={i} className="space-y-2 rounded-md border p-3 surface-subtle" data-testid={`installer-remaining-${i}`}>
                    <div className="flex gap-2"><Input value={r.title} onChange={(e) => setRemaining(remaining.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} placeholder="Tittel" className="input-lg bg-card" data-testid={`installer-remaining-title-${i}`} /><Button variant="ghost" size="icon" className="h-12 w-12 shrink-0" aria-label="Fjern" onClick={() => setRemaining(remaining.filter((_, j) => j !== i))}><Trash2 /></Button></div>
                    <Input value={r.description || ""} onChange={(e) => setRemaining(remaining.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)))} placeholder="Beskrivelse" className="input-lg bg-card" />
                    <Select value={r.type} onValueChange={(v) => setRemaining(remaining.map((x, j) => (j === i ? { ...x, type: v } : x)))}><SelectTrigger className="h-12 bg-card" data-testid={`installer-remaining-type-${i}`}><SelectValue /></SelectTrigger><SelectContent>{Object.entries(TYPE_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>
                    <AiSource quote={r.source} />
                  </div>
                ))}
                {!remaining.length && <p className="rounded-md border border-dashed p-3 text-center text-xs text-muted-foreground">Ingen restpunkter.</p>}
              </div>
            </div>

            <fieldset>
              <legend className="text-sm font-semibold">Resultat</legend>
              <RadioGroup value={result} onValueChange={setResult} className="mt-2 space-y-2" data-testid="installer-result-select">
                {[["montert", "Montert", "released"], ["montert_med_restpunkter", "Montert med restpunkter", "rest"], ["ikke_fullfort", "Ikke fullført – kommer tilbake", "waiting"]].map(([v, l, tone]) => (
                  <label key={v} className={`flex min-h-[52px] cursor-pointer items-center gap-3 rounded-md border px-3.5 text-base transition-colors ${result === v ? "border-primary/50 bg-primary/5 font-medium" : "hover:bg-secondary"}`}>
                    <RadioGroupItem value={v} id={`res-${v}`} data-testid={`installer-result-${v}`} /> <span className="flex-1">{l}</span><span className="h-2 w-2 rounded-full" style={{ background: `hsl(var(--state-${tone}))` }} />
                  </label>
                ))}
              </RadioGroup>
            </fieldset>

          </Card>
        )}
      </div>

      {/* Rendered outside the animated container: transforms would otherwise break position: fixed. */}
      {!done && (
        <div className="sticky-bar">
          <div className="mx-auto flex max-w-xl items-center gap-3">
            <p className="hidden flex-1 text-xs text-muted-foreground sm:block">{result ? "Klar til å lagre." : "Velg resultat for å lagre rapporten."}</p>
            <Button className="touch-btn flex-1 sm:flex-none sm:px-8" onClick={save} disabled={saving || !result} data-testid="installer-save-report-button">{saving && <Loader2 className="animate-spin" />} Lagre rapport</Button>
          </div>
        </div>
      )}

      <Dialog open={basisOpen} onOpenChange={setBasisOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader><DialogTitle>Frigitt grunnlag · rev. {job.released_basis.spec_code}</DialogTitle><DialogDescription>Det som gjelder for montering. Eksempeltegninger – ikke produksjonsgrunnlag.</DialogDescription></DialogHeader>
          <div className="space-y-6">{job.released_basis.drawings.map((x) => <DrawingViewer key={x.id} drawing={{ ...x, status: "gjeldende" }} deliveryNumber={job.number} />)}<SpecTable fields={job.released_basis.spec_fields} testId="installer-spec" /></div>
        </DialogContent>
      </Dialog>
    </InstallerShell>
  );
}
